module Client.Components.SuppliesForm

open Client
open Client.Services
open Shared
open global.Elmish
open Fable.Core.JsInterop
open System
open Thoth.Fetch
open Thoth.Json
open Fetch.Types
open Feliz
open Feliz.MaterialUI
open Fable.MaterialUI.Icons
open Fable.MaterialUI.MaterialDesignIcons
open Feliz.SweetAlert
open Feliz.UseElmish
open Fable.Core.Experimental
open Fable.Core.JS
open FsToolkit.ErrorHandling
open CustomerDexImaging.Models

let strOption (s : string) = if String.IsNullOrWhiteSpace s then None else Some s

let parseInt (s : string) =
  if String.IsNullOrWhiteSpace s
  then None
  else
    match Int32.TryParse s with
    | false, _ -> None
    | true, v -> if v > 0 then Some v else None

type FieldState =
  { Validate : bool
    FieldValue : string option
    FieldWarning : string option
    InitialValue : string option } with
    static member defaultValue =
      { Validate = false; InitialValue = None; FieldValue = None; FieldWarning = None }
    static member init v =
      { Validate = false; InitialValue = v; FieldValue = v; FieldWarning = None }
    member this.setValue v =
      { this with Validate = true; FieldValue = v; FieldWarning = if v.IsNone then Some "A value is required." else None }
    member this.getValue () =
      this.FieldValue |> Result.requireSome "A value is required."

type EntryStateDetail =
  { Id : System.Guid
    EquipmentId : FieldState
    MonoMeter : int option
    ColorMeter : int option
    Make : FieldState
    Model : FieldState
    SuppliesNeeded : FieldState } with
    static member defaultValue () =
      { Id = System.Guid.NewGuid()
        EquipmentId = FieldState.defaultValue
        MonoMeter = None
        ColorMeter = None
        Make = FieldState.defaultValue
        Model = FieldState.defaultValue
        SuppliesNeeded = FieldState.defaultValue }
    static member initForEquipmentNumber equipmentNumber =
      { EntryStateDetail.defaultValue () with
          EquipmentId = FieldState.init (Some equipmentNumber) }

type AdditionalEntryStateDetail =
  { EquipmentId : FieldState
    Make : FieldState
    Model : FieldState
    SuppliesNeeded : FieldState } with
    static member defaultValue =
      { EquipmentId = FieldState.defaultValue
        Make = FieldState.defaultValue
        Model = FieldState.defaultValue
        SuppliesNeeded = FieldState.defaultValue }
    member this.toEntryStateDetail () =
      let d = EntryStateDetail.defaultValue ()
      { d with
          EquipmentId = FieldState.init this.EquipmentId.FieldValue
          Make = FieldState.init this.Make.FieldValue
          Model = FieldState.init this.Model.FieldValue
          SuppliesNeeded = FieldState.init this.SuppliesNeeded.FieldValue }


type EntryState =
  { FirstAndLastName : FieldState
    CompanyName : FieldState
    ShippingAddress : FieldState
    EquipmentLocation : string option
    EmailAddress : FieldState
    BusinessPhone : FieldState
    Items : EntryStateDetail list
    AdditionalEntryStateDetail : AdditionalEntryStateDetail
    FailureMessage : string option } with
    member this.validate () =
      result {
        let! firstAndLastName = this.FirstAndLastName.getValue ()
        let! companyName = this.CompanyName.FieldValue |> Result.requireSome "A value is required."
        let! shippingAddress = this.ShippingAddress.FieldValue |> Result.requireSome "A value is required."
        let! emailAddress = this.EmailAddress.FieldValue |> Result.requireSome "A value is required."
        let! businessPhone = this.BusinessPhone.FieldValue |> Result.requireSome "A value is required."
        let! items =
          match this.Items with
          | [] -> Error "At least one supply entry is required."
          | _ ->
            this.Items
            |> List.map (fun x ->
              result {
                let! equipmentId = x.EquipmentId.FieldValue |> Result.requireSome "A value is required"
                let! make = x.Make.FieldValue |> Result.requireSome "A value is required"
                let! model = x.Model.FieldValue |> Result.requireSome "A value is required"
                let! suppliesNeeded = x.SuppliesNeeded.FieldValue |> Result.requireSome "A value is required"
                let d =
                  { SupplyItemFormRequest.EquipmentId = equipmentId
                    MonoMeter = x.MonoMeter
                    ColorMeter = x.ColorMeter
                    Make = make
                    Model = model
                    SuppliesNeeded = suppliesNeeded }
                return d
              }
            )
            |> List.sequenceResultM
        let validatedEntry =
          { SupplyFormRequest.FirstAndLastName = firstAndLastName
            CompanyName = companyName
            ShippingAddress = shippingAddress
            EquipmentLocation = this.EquipmentLocation
            EmailAddress = emailAddress
            BusinessPhone = businessPhone
            Items = items
            RecaptchaResponse = ""
            FailureMessage = this.FailureMessage
          }

        return validatedEntry
      }
    member this.isValid () =
      match this.validate() with
      | Ok _ -> true
      | Error _ -> false
    static member defaultValue () =
      { FirstAndLastName = FieldState.defaultValue
        CompanyName = FieldState.defaultValue
        ShippingAddress = FieldState.defaultValue
        EquipmentLocation = None
        EmailAddress = FieldState.defaultValue
        BusinessPhone = FieldState.defaultValue
        FailureMessage = None
        Items =
          [ { Id = System.Guid.NewGuid()
              EquipmentId = FieldState.defaultValue
              MonoMeter = None
              ColorMeter = None
              Make = FieldState.defaultValue
              Model = FieldState.defaultValue
              SuppliesNeeded = FieldState.defaultValue }]
        AdditionalEntryStateDetail = AdditionalEntryStateDetail.defaultValue }
    static member init (equipmentId : string) (failureMessage : string) =
      { (EntryState.defaultValue ()) with Items = [ EntryStateDetail.initForEquipmentNumber equipmentId ]; FailureMessage = Some failureMessage }

type Model =
  | Initialized
  | Sending
  | Sent of string

type Msg =
  | SubmitForm of SupplyFormRequest
  | SendingResult of Result<string,string>

let submitForm (request : SupplyFormRequest) : Promise<string> =
  let decoder = Services.decoder<string>
  Fetch.post<_,string>("/api/Supplies/OrderForm", request, decoder = decoder)

let submitFormCmd request =
  Cmd.OfPromise.either submitForm request
    (Ok >> SendingResult)
    (fun x ->
      "Unable to submit request. Please retry" |> Error |> SendingResult)

let init () =
  Initialized, Cmd.none

let update msg model =
  match msg with
  | SubmitForm r ->
    Sending, submitFormCmd r
  | SendingResult r ->
    match r with
    | Ok v -> Sent v, Cmd.none
    | Error msg ->
      let alert =
        Cmd.Swal.fire [
            swal.title "Unable to process request. Please try again."
            swal.text msg ]
      Initialized, alert

let itemEntryView setState (model : EntryState) =
  Html.div [
    for item in model.Items do
      Mui.grid [
        grid.container true
        grid.direction.column
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.children [
              Mui.textField [
                textField.placeholder "Equipment ID (Asset Number)"
                textField.defaultValue (item.EquipmentId.InitialValue |> Option.defaultValue "")
                textField.variant.outlined
                textField.margin.normal
                textField.fullWidth true
                textField.label "Equipment ID"
                textField.required true
                textField.onChange (fun (ev : Browser.Types.Event) ->
                  setState { model with Items = model.Items |> List.map (fun x -> if item.Id <> x.Id then x else { x with EquipmentId = strOption !!ev.target?value |> item.EquipmentId.setValue }); })
              ]
              Mui.textField [
                textField.placeholder "Optional"
                textField.variant.outlined
                textField.margin.normal
                textField.type' "number"
                textField.fullWidth true
                textField.label "Mono Meter"
                textField.required false
                textField.inputProps [ prop.min 0 ]
                textField.onChange (fun (ev : Browser.Types.Event) ->
                  setState { model with Items = model.Items |> List.map (fun x -> if item.Id <> x.Id then x else { x with MonoMeter = parseInt !!ev.target?value }); })
              ]
              Mui.textField [
                textField.placeholder "Optional"
                textField.variant.outlined
                textField.margin.normal
                textField.type' "number"
                textField.fullWidth true
                textField.label "Color Meter"
                textField.required false
                textField.inputProps [ prop.min 0 ]
                textField.onChange (fun (ev : Browser.Types.Event) ->
                  setState { model with Items = model.Items |> List.map (fun x -> if item.Id <> x.Id then x else { x with ColorMeter = parseInt !!ev.target?value }); })
              ]
              Mui.textField [
                textField.placeholder "Equipment Make"
                textField.defaultValue (item.Make.InitialValue |> Option.defaultValue "")
                textField.variant.outlined
                textField.margin.normal
                textField.fullWidth true
                textField.label "Make"
                textField.required true
                textField.onChange (fun (ev : Browser.Types.Event) ->
                  setState { model with Items = model.Items |> List.map (fun x -> if item.Id <> x.Id then x else { x with Make = strOption !!ev.target?value |> item.Make.setValue }); })
              ]
              Mui.textField [
                textField.placeholder "Equipment Model"
                textField.defaultValue (item.Model.InitialValue |> Option.defaultValue "")
                textField.variant.outlined
                textField.margin.normal
                textField.fullWidth true
                textField.label "Model"
                textField.required true
                textField.onChange (fun (ev : Browser.Types.Event) ->
                  setState { model with Items = model.Items |> List.map (fun x -> if item.Id <> x.Id then x else { x with Model = strOption !!ev.target?value |> item.Model.setValue }); })
              ]
              Mui.textField [
                textField.placeholder "Describe the type of supplies you need."
                textField.defaultValue (item.SuppliesNeeded.InitialValue |> Option.defaultValue "")
                textField.variant.outlined
                textField.margin.normal
                textField.fullWidth true
                textField.label "Supplies Needed"
                textField.required true
                textField.onChange (fun (ev : Browser.Types.Event) ->
                  setState { model with Items = model.Items |> List.map (fun x -> if item.Id <> x.Id then x else { x with SuppliesNeeded = strOption !!ev.target?value |> item.SuppliesNeeded.setValue }); })
              ]
            ]
          ]
        ]
      ]

    Mui.hidden [
      hidden.smDown true
      hidden.children [
        Mui.grid [
          grid.container true
          grid.direction.column
          grid.spacing._2
          grid.children [
            Mui.grid [
              grid.item true
              grid.children [
                Mui.typography [
                  typography.variant.h6
                  typography.children "Need more supplies?"
                ]
              ]
            ]
          ]
        ]

        Mui.grid [
          grid.container true
          grid.direction.column
          grid.spacing._2
          grid.children [
            Mui.grid [
              grid.item true
              grid.children [
                Mui.typography [
                  typography.variant.body1
                  typography.children "If you have additional supplies to order, just enter the Make, Model and Equipment ID for each item needed. You can add additional items to your order by pressing the + button."
                ]
              ]
            ]
          ]
        ]
        Mui.grid [
          grid.container true
          grid.item true
          grid.alignContent.center
          grid.alignItems.center
          grid.spacing._2
          grid.children [
            Mui.grid [
              grid.item true
              grid.xs._2
              grid.children [
                Mui.textField [
                  textField.placeholder "Equipment ID"
                  textField.defaultValue (model.AdditionalEntryStateDetail.EquipmentId.InitialValue |> Option.defaultValue "")
                  textField.variant.outlined
                  textField.margin.normal
                  textField.fullWidth true
                  textField.label "Equipment ID"
                  textField.required true
                  textField.onChange (fun (ev : Browser.Types.Event) ->
                    setState { model with AdditionalEntryStateDetail = { model.AdditionalEntryStateDetail with EquipmentId = strOption !!ev.target?value |> model.AdditionalEntryStateDetail.EquipmentId.setValue } })
                ]
              ]
            ]
            Mui.grid [
              grid.item true
              grid.xs._2
              grid.children [
                Mui.textField [
                  textField.placeholder "Equipment make"
                  textField.defaultValue (model.AdditionalEntryStateDetail.Make.InitialValue |> Option.defaultValue "")
                  textField.variant.outlined
                  textField.margin.normal
                  textField.fullWidth true
                  textField.label "Make"
                  textField.required true
                  textField.onChange (fun (ev : Browser.Types.Event) ->
                    setState { model with AdditionalEntryStateDetail = { model.AdditionalEntryStateDetail with Make = strOption !!ev.target?value |> model.AdditionalEntryStateDetail.Make.setValue } })
                ]
              ]
            ]
            Mui.grid [
              grid.item true
              grid.xs._2
              grid.children [
                Mui.textField [
                  textField.placeholder "Equipment model"
                  textField.defaultValue (model.AdditionalEntryStateDetail.Model.InitialValue |> Option.defaultValue "")
                  textField.variant.outlined
                  textField.margin.normal
                  textField.fullWidth true
                  textField.label "Model"
                  textField.required true
                  textField.onChange (fun (ev : Browser.Types.Event) ->
                    setState { model with AdditionalEntryStateDetail = { model.AdditionalEntryStateDetail with Model = strOption !!ev.target?value |> model.AdditionalEntryStateDetail.Model.setValue } })
                ]
              ]
            ]
            Mui.grid [
              grid.item true
              grid.xs._3
              grid.children [
                Mui.textField [
                  textField.placeholder "Supplies needed"
                  textField.defaultValue (model.AdditionalEntryStateDetail.SuppliesNeeded.InitialValue |> Option.defaultValue "")
                  textField.variant.outlined
                  textField.margin.normal
                  textField.fullWidth true
                  textField.label "Supplies needed"
                  textField.required true
                  textField.onChange (fun (ev : Browser.Types.Event) ->
                    setState { model with AdditionalEntryStateDetail = { model.AdditionalEntryStateDetail with SuppliesNeeded = strOption !!ev.target?value |> model.AdditionalEntryStateDetail.SuppliesNeeded.setValue } })
                ]
              ]
            ]
            Mui.grid [
              grid.item true
              grid.xs._1
              grid.children [
                Mui.tooltip [
                  tooltip.title ("Add item")
                  tooltip.children (
                    Mui.fab [
                      fab.size.medium
                      fab.children [ plusIcon []]
                      prop.onClick (fun e ->
                        e.preventDefault ()
                        setState { model with Items = List.append model.Items [model.AdditionalEntryStateDetail.toEntryStateDetail()]}
                      )
                    ])
                ]
              ]
            ]
          ]
        ]
      ]
    ]
  ]

let pendingHeaderMessage =
  Mui.grid [
    grid.container true
    grid.direction.column
    grid.spacing._2
    grid.children [
      Mui.grid [
        grid.item true
        grid.children [
          Mui.typography [
            typography.variant.body1
            typography.children "Please fill out this form to request supplies."
          ]
        ]
      ]
    ]
  ]

let view = React.functionComponent (fun (inputProps: {| initialValues : EntryState option; reCaptchaSiteKey : string; returnResult: unit -> unit; |}) ->
  let state, setState = React.useState(inputProps.initialValues |> Option.defaultWith EntryState.defaultValue)
  let model, dispatch = React.useElmish(init (), update, [| |])
  let warningState, setWarningState = React.useState<string option> None
  let classes = Styles.useStyles()
  let effect () = ReCaptcha.initialize inputProps.reCaptchaSiteKey
  Feliz.React.useEffect (effect , [||])
  match model with
  | Sent msg ->
    Html.div [
      Mui.grid [
        grid.container true
        grid.direction.column
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.children [
              Mui.grid [
                grid.item true
                grid.children [
                  Mui.typography [
                    typography.variant.body1
                    typography.children "Thank you. Your supply request has been received, and you will receive a notification when your request has been processed."
                  ]
                ]
              ]
            ]
          ]
        ]
      ]
      Mui.grid [
        grid.container true
        grid.direction.column
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.children [
              Mui.grid [
                grid.item true
                grid.children [
                  Mui.button [
                    prop.type'.submit
                    button.fullWidth true
                    button.variant.contained
                    button.color.primary
                    button.children "Add Another Request"
                    prop.onClick (fun e ->
                      e.preventDefault ()
                      inputProps.returnResult()
                    )
                  ]
                ]
              ]
            ]
          ]
        ]
      ]
    ]
  | Sending
  | Initialized ->
    Html.div [
      pendingHeaderMessage
      Mui.grid [
        grid.container true
        grid.direction.column
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.children [
              Html.form [
                prop.children [
                  Mui.textField [
                    textField.placeholder "Enter Your First and Last Name"
                    textField.defaultValue (state.FirstAndLastName.InitialValue |> Option.defaultValue "")
                    textField.variant.outlined
                    textField.margin.normal
                    textField.fullWidth true
                    textField.label "First & Last Name"
                    textField.name "FirstAndLastName"
                    textField.required true
                    textField.onChange (fun (ev : Browser.Types.Event) -> setState { state with FirstAndLastName = strOption !!ev.target?value |> state.FirstAndLastName.setValue })
                  ]
                  Mui.textField [
                    textField.placeholder "Company Name"
                    textField.defaultValue (state.CompanyName.InitialValue |> Option.defaultValue "")
                    textField.variant.outlined
                    textField.margin.normal
                    textField.fullWidth true
                    textField.label "Company"
                    textField.name "CompanyName"
                    textField.required true
                    textField.onChange (fun (ev : Browser.Types.Event) -> setState { state with CompanyName = strOption !!ev.target?value |> state.CompanyName.setValue })
                  ]
                  Mui.textField [
                    textField.placeholder ""
                    textField.defaultValue (state.ShippingAddress.InitialValue |> Option.defaultValue "")
                    textField.variant.outlined
                    textField.margin.normal
                    textField.fullWidth true
                    textField.label "Shipping Address"
                    textField.name "ShippingAddress"
                    textField.required true
                    textField.onChange (fun (ev : Browser.Types.Event) -> setState { state with ShippingAddress = strOption !!ev.target?value |> state.ShippingAddress.setValue })
                  ]
                  Mui.textField [
                    textField.placeholder ""
                    textField.variant.outlined
                    textField.margin.normal
                    textField.fullWidth true
                    textField.label "Equipment Location (if different)"
                    textField.name "EquipmentLocation"
                    textField.required false
                    textField.onChange (fun (ev : Browser.Types.Event) -> setState { state with EquipmentLocation = strOption !!ev.target?value })
                  ]
                  let emailHandler emailOption = setState { state with EmailAddress = state.EmailAddress.setValue emailOption }
                  Components.EmailAddress.view {| initialValue = state.EmailAddress.InitialValue; allowEntry = true; isRequired = true; returnResult = emailHandler |}
                  Components.PhoneNumber.view {| initialValue = state.BusinessPhone.InitialValue; allowEntry = true; isRequired = true; returnResult = (fun v -> setState { state with BusinessPhone = state.BusinessPhone.setValue v }) |}
                  itemEntryView setState state

                  let buttonProgressIndicator =
                    Mui.circularProgress [
                      prop.className classes.buttonProgress
                      prop.size 24
                    ]


                  Mui.grid [
                    grid.container true
                    grid.spacing._2
                    grid.children [
                      Mui.grid [
                        grid.item true
                        grid.xs._6
                        grid.children [
                          Html.div [
                            prop.className classes.wrapper
                            prop.children [
                              Mui.button [
                                prop.type'.submit
                                button.fullWidth true
                                button.variant.contained
                                button.color.primary
                                button.children "Submit"
                                button.disabled (state.isValid() |> not || model = Sending)
                                prop.onClick (fun e ->
                                  e.preventDefault ()
                                  match state.validate() with
                                  | Ok v ->
                                    // inputProps.returnResult () // (Some v)
                                    let fn token =
                                      SubmitForm { v with RecaptchaResponse = token; } |> dispatch
                                    ReCaptcha.registerCallBack inputProps.reCaptchaSiteKey Constants.recaptchaRequestSuppliesAction fn
                                  | Error msg -> setWarningState (Some msg)
                                )
                              ]
                              if model = Sending then buttonProgressIndicator else Html.none
                            ]
                          ]
                        ]
                      ]
                      Mui.grid [
                        grid.item true
                        grid.xs._6
                        grid.children [
                          Mui.button [
                            button.fullWidth true
                            button.variant.contained
                            button.color.default'
                            button.children "Cancel"
                            prop.onClick (fun e ->
                              e.preventDefault ()
                              inputProps.returnResult()
                            )
                          ]
                        ]
                      ]
                      if warningState.IsSome then Html.text warningState.Value else Html.none
                    ]
                  ]
                ]
              ]
            ]
          ]
        ]
      ]
    ]
)